import React, { useCallback, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import { TtButton, TtPoll, TtPollAnswer } from '@23/react'

import { type Poll } from '../../../models/poll'
import {
  useInvalidatePolls,
  usePollsQuery,
  useTogglePollResultsMutation,
} from '../../../hooks/use-polls'

interface ClosedPollProps {
  poll: Poll
}

const ClosedPoll = ({ poll }: ClosedPollProps) => {
  const { data: polls } = usePollsQuery()
  const { mutateAsync: togglePollResults } = useTogglePollResultsMutation(
    poll.id
  )

  const [isLoading, setIsLoading] = React.useState(false)

  const invalidatePollsQuery = useInvalidatePolls()

  const pollData = useMemo(() => {
    return polls?.find((p) => p.id === poll.id)
  }, [polls, poll.id])

  const handleClick = useCallback(async () => {
    if (!pollData) return

    setIsLoading(true)
    try {
      await togglePollResults(!pollData.isDisplayingResults)
      await invalidatePollsQuery()
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }, [invalidatePollsQuery, pollData, togglePollResults])

  return (
    <>
      <TtPoll label={poll.question}>
        {poll.options.map((option) => (
          <TtPollAnswer
            key={option.id}
            label={option.option}
            value={option.count}
          />
        ))}
      </TtPoll>
      {pollData && (
        <TtButton
          color="secondary"
          variant={pollData.isDisplayingResults ? 'outline' : 'default'}
          size="small"
          pill
          onClick={handleClick}
          disabled={isLoading}
          loading={isLoading}
        >
          {pollData.isDisplayingResults ? (
            <FormattedMessage defaultMessage="Stop Showing Results" />
          ) : (
            <FormattedMessage defaultMessage="Show Results" />
          )}
        </TtButton>
      )}
    </>
  )
}

export { ClosedPoll }
